import React, { useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { Skeleton } from "@/src/components/ui/skeleton";
import { cn } from "@/src/lib/utils";

import { getCountryLabel, getMapShade, getValueRange } from "./countries.utils";
import useWorldMap from "./useWorldMap";

import type { JSX, PointerEvent, ReactNode } from "react";

interface CountriesMapProps {
  valueByCountry: Readonly<Record<string, number>>;
  formatValue: (value: number) => string;
  renderTooltip: (countryCode: string) => ReactNode;
  ariaLabel: string;
}

interface HoveredCountry {
  code: string;
  x: number;
  y: number;
  isRightHalf: boolean;
}

function finiteOrZero(value: number): number {
  return Number.isFinite(value) ? value : 0;
}

function CountriesMap({
  valueByCountry,
  formatValue,
  renderTooltip,
  ariaLabel,
}: CountriesMapProps): JSX.Element {
  const { t, i18n } = useTranslation("editor-analytics");
  const lang = i18n.language;
  const { data: worldMap, isError } = useWorldMap();
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredCountry, setHoveredCountry] = useState<HoveredCountry | null>(
    null,
  );
  const range = useMemo(() => getValueRange(valueByCountry), [valueByCountry]);

  if (isError) {
    return (
      <p className="text-destructive flex aspect-[9/4] items-center justify-center text-sm">
        {t("charts.error")}
      </p>
    );
  }
  if (!worldMap) return <Skeleton className="aspect-[9/4] w-full" />;

  const hoveredOutline = hoveredCountry
    ? worldMap.countryList.find((country) => country.id === hoveredCountry.code)
    : undefined;

  const handlePointerMove =
    (code: string) =>
    (event: PointerEvent<SVGPathElement>): void => {
      const rect = containerRef.current?.getBoundingClientRect();
      const x = finiteOrZero(event.clientX) - (rect?.left ?? 0);
      setHoveredCountry({
        code,
        x,
        y: finiteOrZero(event.clientY) - (rect?.top ?? 0),
        isRightHalf: x > (rect?.width ?? 0) / 2,
      });
    };

  return (
    <div ref={containerRef} className="relative">
      <svg
        aria-label={ariaLabel}
        className="h-auto w-full"
        role="img"
        viewBox={worldMap.viewBox}
      >
        {worldMap.countryList.map((country, countryIndex) => {
          const value = country.id ? valueByCountry[country.id] : undefined;
          const shade =
            country.id && range && value !== undefined && value > 0
              ? getMapShade(value, range)
              : undefined;

          return (
            <path
              key={country.id ?? `unnamed-${countryIndex}`}
              d={country.d}
              data-country={country.id ?? undefined}
              fillOpacity={shade}
              className={cn(
                "stroke-card stroke-[0.5]",
                shade === undefined ? "fill-muted" : "fill-chart-1",
              )}
              onPointerLeave={
                shade === undefined ? undefined : () => setHoveredCountry(null)
              }
              onPointerMove={
                shade === undefined || !country.id
                  ? undefined
                  : handlePointerMove(country.id)
              }
            >
              {shade !== undefined && country.id && value !== undefined ? (
                <title>{`${getCountryLabel(country.id, lang)}, ${formatValue(value)}`}</title>
              ) : null}
            </path>
          );
        })}
        {hoveredOutline ? (
          <path
            aria-hidden
            className="stroke-chart-1 pointer-events-none fill-none stroke-1"
            d={hoveredOutline.d}
            strokeLinejoin="round"
          />
        ) : null}
      </svg>
      {hoveredCountry ? (
        <div
          role="tooltip"
          style={{ left: hoveredCountry.x, top: hoveredCountry.y }}
          className={cn(
            "border-border/50 bg-background pointer-events-none absolute z-10 grid min-w-[8rem] gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl",
            hoveredCountry.isRightHalf
              ? "-translate-x-[calc(100%+12px)]"
              : "translate-x-3",
          )}
        >
          <span className="font-medium">
            {getCountryLabel(hoveredCountry.code, lang)}
          </span>
          {renderTooltip(hoveredCountry.code)}
        </div>
      ) : null}
      {range ? (
        <div className="text-muted-foreground mx-auto mt-3 flex max-w-sm items-center gap-2 text-xs tabular-nums">
          {range.min === range.max ? (
            <span className="mx-auto">{formatValue(range.max)}</span>
          ) : (
            <React.Fragment>
              <span>{formatValue(range.min)}</span>
              <div className="from-chart-1/25 to-chart-1 h-2 flex-1 rounded-full bg-linear-to-r" />
              <span>{formatValue(range.max)}</span>
            </React.Fragment>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default CountriesMap;
